import type { FeedbackCommentDto, FeedbackReplyDto } from '@crm/shared'

/**
 * Сообщения переписки по обращению: комментарий человека внутри CRM и ответ
 * поддержки из «Пульта проектов». Вынесены из окна обращения, чтобы оно не
 * перевалило за 500 строк. Раздел «Обратная связь» заморожен: встроить ответы
 * поддержки в переписку разрешил Сергей 14.09.2026; комментарий перенесён без
 * изменений.
 */

function initials(name: string | null | undefined, email: string | undefined) {
  if (name) return name.slice(0, 2).toUpperCase()
  return (email ?? '?').slice(0, 2).toUpperCase()
}

export function CommentBubble({
  comment,
  isOwn,
  canDelete,
  onDelete,
}: {
  comment: FeedbackCommentDto & { isLegacy?: boolean }
  isOwn: boolean
  canDelete: boolean
  onDelete: () => void
}) {
  const isAdmin = comment.author?.role === 'ADMIN'
  return (
    <div className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}>
      <div
        className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-semibold text-white ${isAdmin ? 'bg-brand-600' : 'bg-gray-500'}`}
      >
        {initials(comment.author?.name, comment.author?.email)}
      </div>
      <div
        className={`flex-1 max-w-[80%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col gap-0.5`}
      >
        <div
          className={`flex items-center gap-1.5 text-xs text-gray-500 ${isOwn ? 'flex-row-reverse' : ''}`}
        >
          <span className="font-medium text-gray-800">
            {comment.author?.name ?? comment.author?.email ?? '—'}
          </span>
          {isAdmin && (
            <span className="px-1.5 py-0.5 rounded bg-brand-100 text-brand-700 font-medium">
              Администратор
            </span>
          )}
          <span>·</span>
          <span>{comment.author?.email}</span>
          <span>·</span>
          <span>{new Date(comment.createdAt).toLocaleString('ru-RU')}</span>
          {comment.isLegacy && <span className="text-gray-400">(старый ответ)</span>}
        </div>
        <div
          className={`rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
            isAdmin
              ? 'bg-brand-50 border border-brand-200 text-brand-900'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {comment.text}
          {comment.screenshot && (
            <a
              href={`/api/uploads/feedback/${comment.screenshot}`}
              target="_blank"
              rel="noreferrer"
              className="block mt-2"
            >
              <img
                src={`/api/uploads/feedback/${comment.screenshot}`}
                alt="скриншот"
                className="max-h-60 rounded border"
              />
            </a>
          )}
        </div>
        {canDelete && !comment.isLegacy && (
          <button
            onClick={onDelete}
            className={`text-xs text-red-400 hover:text-red-600 ${isOwn ? 'self-end' : 'self-start'}`}
          >
            Удалить
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Вложение ответа поддержки. Картинка — превью со ссылкой на полный размер,
 * PDF — ссылка на скачивание (сервер и так отдаёт pdf только скачиванием).
 */
function ReplyAttachment({ name, spaced }: { name: string; spaced: boolean }) {
  const url = `/api/uploads/feedback/${name}`
  const gap = spaced ? 'mt-2 ' : ''
  if (name.toLowerCase().endsWith('.pdf')) {
    return (
      <a
        href={url}
        download
        className={`${gap}flex items-start gap-1 text-brand-600 hover:text-brand-700 underline break-all`}
      >
        <span aria-hidden>📄</span>
        <span>{name}</span>
      </a>
    )
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className={`${gap}block`}>
      <img src={url} alt="вложение" className="max-h-60 max-w-full rounded border" />
    </a>
  )
}

/** Ответ поддержки: как сообщение администратора, но с бейджем «Поддержка». */
export function SupportReplyBubble({ reply }: { reply: FeedbackReplyDto }) {
  return (
    <div className="flex gap-2">
      <div
        aria-hidden
        className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-semibold text-white bg-brand-600"
      >
        П
      </div>
      <div className="flex-1 min-w-0 max-w-[80%] items-start flex flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
          <span className="px-1.5 py-0.5 rounded bg-brand-100 text-brand-700 font-medium">
            Поддержка
          </span>
          <span>·</span>
          <span>{new Date(reply.createdAt).toLocaleString('ru-RU')}</span>
        </div>
        <div className="rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words bg-brand-50 border border-brand-200 text-brand-900">
          {reply.text}
          {reply.files.map((name, i) => (
            <ReplyAttachment key={name} name={name} spaced={Boolean(reply.text) || i > 0} />
          ))}
        </div>
      </div>
    </div>
  )
}
